import type { RawBrandingConfig } from '../branding'
import type { ProviderExecutionRecipe, ProviderExecutionRow } from '../types/feedContract'
import { formatMoney } from '../utils/money'
import { calculatePayout } from '../utils/odds'

const POSITIVE_INTEGER_PATTERN = /^[1-9]\d*$/

/** Kit hints for the jersey the renderer draws; every field is validated before use. */
export interface PrepackLegTeamKit {
  pattern?: string
  body?: string
  trim?: string
  number_color?: string
}

/**
 * The team a leg backs, resolved server-side (#572). The renderer draws a
 * jersey from `colors`/`kit` and shows `logo_url` only from an allowed https host.
 */
export interface PrepackLegTeam {
  id?: string
  name: string
  abbr?: string
  colors?: string[]
  kit?: PrepackLegTeamKit
  /** The team's alternate uniform, from its primary kit whichever variant is applied; Studio previews the alternate with it. */
  alternate_kit?: PrepackLegTeamKit
  number?: string
  logo_url?: string
  /** 'alternate' when Studio applied the operator's alternate kit to `kit` (or `colors`). */
  kit_variant?: 'alternate'
}

export interface PrepackLeg {
  id?: string
  market_id?: string
  regulation?: string
  outcome_id: string | number
  event_id: string
  event_label?: string
  event_start_time?: string
  sport?: string
  league?: string
  market_label?: string
  market_name?: string
  participant?: string
  participant_id?: string
  team_participant_id?: string
  stat_category?: string
  line?: number | string
  outcome_role?: string
  period?: string
  selection_name?: string
  /** The sportsbook's own words for the bet ("To Score", "Anytime Touchdown Scorer"), printed under the participant (#865). */
  bet_label?: string
  odds?: string
  decimal_odds?: number
  american_odds?: string
  team?: PrepackLegTeam
  [key: string]: unknown
}

export interface PublicPrepack {
  id: string
  title: string
  subtitle?: string
  regulation: string
  pack_type: string
  bet_type?: string | null
  /** Operator labels from Studio (at most five, 40 characters each); the card's shell lists them as chips. */
  tags?: string[]
  odds: string
  payout_multiplier: number
  template_id?: string | null
  template_vars?: Record<string, unknown>
  legs: PrepackLeg[]
  updated_at: string
  valid_until?: string
  copies?: number
  sport?: string
  league?: string
  event?: string
  event_id?: string
  event_label?: string
  [key: string]: unknown
}

export interface PrepackTemplateVariable {
  key: string
  label?: string
  value: string
  type?: string
}

export interface PrepackTemplateVariant {
  cssVars: PrepackTemplateVariable[]
  cssCode: string
  branding: RawBrandingConfig
}

export interface PrepackCardTemplate {
  id: string
  name: string
  description?: string
  htmlCode?: string
  cssCode?: string
  cssVars?: PrepackTemplateVariable[]
  isDefault?: boolean
  isFallback?: boolean
  variants?: Partial<Record<'light' | 'dark', Partial<PrepackTemplateVariant>>>
  previousArtwork?: Array<{ htmlCode: string; cssCode: string; cssVars?: PrepackTemplateVariable[]; variants?: PrepackCardTemplate['variants']; name?: string; description?: string }>
  /** What the native action under artwork shows: the call to action (default) or the quoted odds and payout. */
  nativeAction?: 'label' | 'odds'
  previewPayload?: Record<string, unknown>
}

export interface PrepackSurfaceLayoutConfig {
  card_density?: 'compact' | 'comfortable' | 'spacious'
  dimension?: string
  max_display_count?: number
  cta_label?: string
  show_odds_badge?: boolean
  example_stake?: number
}

export const DEFAULT_EXAMPLE_STAKE = 10

export { sportGlyph } from '../../../shared/pkg/prepacks/templateRendering.js'

/** Sanitized example stake in whole dollars; invalid config falls back to the default. */
export function resolveExampleStake(value: unknown): number {
  const stake = Number(value)
  return Number.isFinite(stake) && stake > 0 ? stake : DEFAULT_EXAMPLE_STAKE
}

/** "$10 pays $1,035" from the server multiplier, falling back to the quoted American odds. */
export function prepackPayoutLabel(pack: Pick<PublicPrepack, 'odds' | 'payout_multiplier'>, stake: number, { currency = 'USD', locale = 'en-US' }: { currency?: string; locale?: string } = {}): string {
  const multiplier = Number(pack.payout_multiplier)
  let payout: number | null = null
  if (Number.isFinite(multiplier) && multiplier > 1) {
    payout = stake * multiplier
  } else {
    const computed = Number(calculatePayout(pack.odds || '', stake))
    payout = Number.isFinite(computed) && computed > stake ? computed : null
  }
  if (payout == null) return ''
  return `${formatMoney(stake, currency, locale, { wholeWithoutCents: true })} pays ${formatMoney(payout, currency, locale, { wholeWithoutCents: true })}`
}

/** Footer counter label; empty when nothing has been copied yet. */
export function prepackCopiesLabel(copies: unknown): string {
  const count = Number(copies)
  if (!Number.isFinite(count) || count <= 0) return ''
  const rounded = Math.floor(count)
  return `${rounded.toLocaleString('en-US')} ${rounded === 1 ? 'bet' : 'bets'}`
}

export interface PrepackSurfaceData {
  slug: string
  name: string
  display_type?: string
  layout_config?: PrepackSurfaceLayoutConfig
}

export interface PublicPrepacksDeliveryResponse {
  version: number
  regulation: string
  valid_until: string
  items: PublicPrepack[]
  templates: PrepackCardTemplate[]
  odds_format?: string
  surface: PrepackSurfaceData
  generated_at?: string
}

export function parseSafeOutcomeId(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isInteger(raw) && raw > 0 && Number.isSafeInteger(raw)) {
    return raw
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (POSITIVE_INTEGER_PATTERN.test(trimmed)) {
      const parsed = Number(trimmed)
      if (Number.isSafeInteger(parsed) && parsed > 0) {
        return parsed
      }
    }
  }
  return null
}

export function parseValidUntil(raw: unknown): number | null {
  if (typeof raw !== 'string') return null
  const trimmed = raw.trim()
  if (!trimmed) return null
  const ms = Date.parse(trimmed)
  return Number.isFinite(ms) ? ms : null
}

export function isDeliveryLeaseExpired(
  response: PublicPrepacksDeliveryResponse | { valid_until?: unknown } | null | undefined,
  now = Date.now(),
): boolean {
  if (!response || typeof response !== 'object') return true
  const lease = parseValidUntil(response.valid_until)
  if (lease === null) return true
  return lease <= now
}

export function isPrepackExpired(pack: PublicPrepack, now = Date.now()): boolean {
  if (!pack || typeof pack !== 'object') return true
  if (pack.valid_until !== undefined && pack.valid_until !== null) {
    const packLease = parseValidUntil(pack.valid_until)
    if (packLease === null || packLease <= now) {
      return true
    }
  }
  if (!pack.legs || pack.legs.length === 0) return false
  const starts = pack.legs
    .map((leg) => (leg.event_start_time ? Date.parse(leg.event_start_time) : NaN))
    .filter(Number.isFinite)
  if (starts.length === 0) return false
  return Math.min(...starts) <= now
}

export function earliestPrepackExpiry(
  packs: PublicPrepack[],
  now = Date.now(),
  deliveryValidUntil?: string | null,
): number | null {
  const futureStarts: number[] = []

  if (deliveryValidUntil) {
    const deliveryLease = parseValidUntil(deliveryValidUntil)
    if (deliveryLease !== null && deliveryLease > now) {
      futureStarts.push(deliveryLease)
    }
  }

  for (const pack of packs) {
    if (pack.valid_until) {
      const packLease = parseValidUntil(pack.valid_until)
      if (packLease !== null && packLease > now) {
        futureStarts.push(packLease)
      }
    }
    for (const leg of pack.legs || []) {
      if (leg.event_start_time) {
        const time = Date.parse(leg.event_start_time)
        if (Number.isFinite(time) && time > now) {
          futureStarts.push(time)
        }
      }
    }
  }
  return futureStarts.length > 0 ? Math.min(...futureStarts) : null
}

export function hasMatchingPackIdentity(
  originalPack: PublicPrepack,
  freshPack: PublicPrepack,
): boolean {
  if (
    !originalPack ||
    !freshPack ||
    typeof originalPack !== 'object' ||
    typeof freshPack !== 'object'
  ) {
    return false
  }
  if (!originalPack.id || originalPack.id !== freshPack.id) return false
  if (!originalPack.pack_type || originalPack.pack_type !== freshPack.pack_type) return false

  const origReg = (originalPack.regulation || '').trim()
  const freshReg = (freshPack.regulation || '').trim()
  if (!origReg || origReg !== freshReg) {
    return false
  }

  const origLegs = originalPack.legs || []
  const freshLegs = freshPack.legs || []
  if (origLegs.length !== freshLegs.length || origLegs.length === 0) {
    return false
  }

  const origOutcomeIds = new Set<number>()
  for (const leg of origLegs) {
    const id = parseSafeOutcomeId(leg.outcome_id)
    if (id === null || origOutcomeIds.has(id)) {
      return false
    }
    origOutcomeIds.add(id)
  }

  const freshLegsByOutcomeId = new Map<number, PrepackLeg>()
  for (const leg of freshLegs) {
    const id = parseSafeOutcomeId(leg.outcome_id)
    if (id === null || freshLegsByOutcomeId.has(id)) {
      return false
    }
    freshLegsByOutcomeId.set(id, leg)
  }

  if (origOutcomeIds.size !== freshLegsByOutcomeId.size) {
    return false
  }

  for (const oLeg of origLegs) {
    const oId = parseSafeOutcomeId(oLeg.outcome_id)!
    const fLeg = freshLegsByOutcomeId.get(oId)
    if (!fLeg) {
      return false
    }

    const oEvent = (oLeg.event_id || '').trim()
    const fEvent = (fLeg.event_id || '').trim()
    if (!oEvent || oEvent !== fEvent) {
      return false
    }

    const oMarketId = (oLeg.market_id || '').trim()
    const fMarketId = (fLeg.market_id || '').trim()
    if (oMarketId || fMarketId) {
      if (oMarketId !== fMarketId) return false
    }

    const oMarketLabel = (oLeg.market_label || oLeg.market_name || '').trim()
    const fMarketLabel = (fLeg.market_label || fLeg.market_name || '').trim()
    if (!oMarketId && !fMarketId) {
      if (oMarketLabel !== fMarketLabel) return false
    } else if (oMarketLabel && fMarketLabel && oMarketLabel !== fMarketLabel) {
      return false
    }

    const oLegReg = (oLeg.regulation || '').trim()
    const fLegReg = (fLeg.regulation || '').trim()
    if (oLegReg && fLegReg && oLegReg !== fLegReg) {
      return false
    }

    const oPart = (oLeg.participant || '').trim()
    const fPart = (fLeg.participant || '').trim()
    if (oPart !== fPart) {
      return false
    }

    const oSel = (oLeg.selection_name || '').trim()
    const fSel = (fLeg.selection_name || '').trim()
    if (oSel !== fSel) {
      return false
    }

    if (!oSel && !oPart) {
      return false
    }

    if (oLeg.line !== undefined || fLeg.line !== undefined) {
      if (String(oLeg.line ?? '') !== String(fLeg.line ?? '')) {
        return false
      }
    }
  }

  return true
}

export function isPriceOnlyUpdate(
  originalPack: PublicPrepack,
  freshPack: PublicPrepack,
): boolean {
  if (!hasMatchingPackIdentity(originalPack, freshPack)) return false
  return (
    originalPack.odds !== freshPack.odds ||
    originalPack.payout_multiplier !== freshPack.payout_multiplier
  )
}

export function buildSGPExecutionRecipe(
  legs: PrepackLeg[],
  outcomeIds: number[],
): ProviderExecutionRecipe | null {
  if (outcomeIds.length < 2) return null

  const uniqueOutcomeIds = new Set(outcomeIds)
  if (uniqueOutcomeIds.size !== outcomeIds.length) {
    return null
  }

  const outcomeToLeg = new Map<string, PrepackLeg>()
  for (const leg of legs) {
    const id = parseSafeOutcomeId(leg.outcome_id)
    if (id !== null) {
      outcomeToLeg.set(String(id), leg)
    }
  }

  for (const outcomeId of outcomeIds) {
    if (!outcomeToLeg.has(String(outcomeId))) {
      return null
    }
  }

  const eventGroups = new Map<string, string[]>()
  for (const outcomeId of outcomeIds) {
    const leg = outcomeToLeg.get(String(outcomeId))
    const eventKey = leg?.event_id || `single-${outcomeId}`
    const existing = eventGroups.get(eventKey) || []
    existing.push(String(outcomeId))
    eventGroups.set(eventKey, existing)
  }

  let builderRows = 0
  for (const outcomes of eventGroups.values()) {
    if (outcomes.length >= 2) builderRows += 1
  }

  // Kambi WAPI contract allows at most 1 BET_BUILDER row
  if (builderRows > 1) {
    return null
  }

  const rows: ProviderExecutionRow[] = []
  let rowIndex = 0

  for (const outcomes of eventGroups.values()) {
    if (outcomes.length >= 2) {
      rows.push({
        index: rowIndex++,
        type: 'BET_BUILDER',
        outcome_ids: outcomes,
      })
    }
  }

  for (const outcomes of eventGroups.values()) {
    if (outcomes.length === 1) {
      rows.push({
        index: rowIndex++,
        type: 'SIMPLE',
        outcome_ids: outcomes,
      })
    }
  }

  return {
    recipe_version: 1,
    provider: 'kambi',
    rows,
    coupon_row_indexes: rows.map((r) => r.index),
    each_way: false,
  }
}

export interface PrepackGroup {
  id: string
  name: string
  items: PublicPrepack[]
}

export function groupPrepacks(
  items: PublicPrepack[],
  dimension?: string | null,
): PrepackGroup[] {
  if (!dimension || dimension === 'none') {
    return [{ id: 'all', name: '', items }]
  }

  const groupMap = new Map<string, PrepackGroup>()

  for (const item of items) {
    let key: string
    let name: string

    if (dimension === 'event') {
      const firstLeg = item.legs?.[0]
      const rawEventId =
        firstLeg?.event_id ??
        (item.event_id as string | number | undefined) ??
        (item.event as string | undefined)

      const eventId =
        rawEventId !== undefined && rawEventId !== null && String(rawEventId).trim() !== ''
          ? String(rawEventId).trim()
          : 'Featured'

      const rawLabel =
        firstLeg?.event_label ??
        (item.event_label as string | undefined)

      const trimmedLabel = typeof rawLabel === 'string' ? rawLabel.trim() : ''
      name = trimmedLabel || eventId
      key = eventId
    } else {
      const rawVal = item[dimension] ?? item.legs?.[0]?.[dimension]
      key =
        rawVal !== undefined && rawVal !== null && String(rawVal).trim() !== ''
          ? String(rawVal).trim()
          : 'Featured'
      name = key
    }

    const existing = groupMap.get(key)
    if (existing) {
      if (dimension === 'event' && existing.name === existing.id && name !== key) {
        existing.name = name
      }
      existing.items.push(item)
    } else {
      groupMap.set(key, { id: key, name, items: [item] })
    }
  }

  return Array.from(groupMap.values())
}

export async function readJsonWithSignal<T>(
  res: { json: () => Promise<unknown> },
  signal?: AbortSignal,
): Promise<T> {
  if (signal?.aborted) {
    throw new DOMException('The operation was aborted', 'AbortError')
  }

  if (!signal) {
    return (await res.json()) as T
  }

  return new Promise<T>((resolve, reject) => {
    let settled = false

    const onAbort = () => {
      if (settled) return
      settled = true
      signal.removeEventListener('abort', onAbort)
      reject(new DOMException('The operation was aborted', 'AbortError'))
    }

    signal.addEventListener('abort', onAbort, { once: true })

    res
      .json()
      .then((data) => {
        if (settled) return
        settled = true
        signal.removeEventListener('abort', onAbort)
        if (signal.aborted) {
          reject(new DOMException('The operation was aborted', 'AbortError'))
        } else {
          resolve(data as T)
        }
      })
      .catch((err) => {
        if (settled) return
        settled = true
        signal.removeEventListener('abort', onAbort)
        reject(err)
      })
  })
}

export interface DeliveryFreshnessTracker {
  nextSeq(): number
  currentSeq(): number
  latestAppliedSeq(): number
  surfaceRevocationSeq(): number
  packRevocationSeq(packId: string): number
  canApply(seq: number, packId?: string): boolean
  applyDelivery(seq: number, data: PublicPrepacksDeliveryResponse | null, now?: number): boolean
  recordUnavailability(seq: number, packId?: string): void
  reconcileItems(seq: number, items: PublicPrepack[], now?: number): void
  isActionRevokedOrStale(seq: number, packId: string): boolean
  resetScope(): void
}

export function createDeliveryFreshnessTracker(): DeliveryFreshnessTracker {
  let deliverySeq = 0
  let latestApplied = 0
  let surfaceRevocation = 0
  const packRevocations = new Map<string, number>()
  const activePackIds = new Set<string>()

  return {
    nextSeq(): number {
      deliverySeq += 1
      return deliverySeq
    },
    currentSeq(): number {
      return deliverySeq
    },
    latestAppliedSeq(): number {
      return latestApplied
    },
    surfaceRevocationSeq(): number {
      return surfaceRevocation
    },
    packRevocationSeq(packId: string): number {
      return packRevocations.get(packId) ?? 0
    },
    canApply(seq: number, packId?: string): boolean {
      if (seq < latestApplied) {
        return false
      }
      if (seq < surfaceRevocation) {
        return false
      }
      if (packId && seq < (packRevocations.get(packId) ?? 0)) {
        return false
      }
      return true
    },
    applyDelivery(
      seq: number,
      data: PublicPrepacksDeliveryResponse | null,
      now = Date.now(),
    ): boolean {
      if (seq < latestApplied) {
        return false
      }
      if (seq < surfaceRevocation) {
        return false
      }
      latestApplied = Math.max(latestApplied, seq)

      if (!data || isDeliveryLeaseExpired(data, now) || !data.items || data.items.length === 0) {
        surfaceRevocation = Math.max(surfaceRevocation, seq)
        activePackIds.clear()
        return true
      }

      const newPackIds = new Set<string>()
      for (const item of data.items) {
        if (item?.id) {
          if (isPrepackExpired(item, now)) {
            packRevocations.set(item.id, Math.max(packRevocations.get(item.id) ?? 0, seq))
          } else {
            newPackIds.add(item.id)
          }
        }
      }

      for (const oldId of activePackIds) {
        if (!newPackIds.has(oldId)) {
          packRevocations.set(oldId, Math.max(packRevocations.get(oldId) ?? 0, seq))
        }
      }

      activePackIds.clear()
      for (const id of newPackIds) {
        activePackIds.add(id)
      }

      return true
    },
    recordUnavailability(seq: number, packId?: string): void {
      latestApplied = Math.max(latestApplied, seq)
      if (packId) {
        packRevocations.set(packId, Math.max(packRevocations.get(packId) ?? 0, seq))
        activePackIds.delete(packId)
      } else {
        surfaceRevocation = Math.max(surfaceRevocation, seq)
        activePackIds.clear()
      }
    },
    reconcileItems(seq: number, items: PublicPrepack[], now = Date.now()): void {
      const incomingIds = new Set<string>()
      for (const item of items) {
        if (item?.id) {
          if (isPrepackExpired(item, now)) {
            packRevocations.set(item.id, Math.max(packRevocations.get(item.id) ?? 0, seq))
          } else {
            incomingIds.add(item.id)
          }
        }
      }
      for (const oldId of activePackIds) {
        if (!incomingIds.has(oldId)) {
          packRevocations.set(oldId, Math.max(packRevocations.get(oldId) ?? 0, seq))
        }
      }
      activePackIds.clear()
      for (const id of incomingIds) {
        activePackIds.add(id)
      }
    },
    isActionRevokedOrStale(seq: number, packId: string): boolean {
      if (seq < latestApplied) {
        return true
      }
      if (seq < surfaceRevocation) {
        return true
      }
      const packRev = packRevocations.get(packId) ?? 0
      if (seq < packRev) {
        return true
      }
      return false
    },
    resetScope(): void {
      deliverySeq += 1
      latestApplied = deliverySeq
      surfaceRevocation = deliverySeq
      packRevocations.clear()
      activePackIds.clear()
    },
  }
}
